import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Cpu, Gauge, Wifi, Sun, Battery, Wrench, Activity, ShieldCheck, Factory, Boxes, Award, Layers, TrendingUp } from "lucide-react";

const FACTORY = {
    hero: "/cubenz/factory_09.jpeg",
    assemblyLine: "/cubenz/factory_11.jpeg",
    team: "/cubenz/factory_14.png",
    finished: "/cubenz/factory_15.jpeg",
    detail1: "/cubenz/factory_16.jpeg",
    detail2: "/cubenz/factory_17.jpeg",
    detail3: "/cubenz/factory_18.jpeg",
    detail4: "/cubenz/factory_19.jpeg",
    packaged: "/cubenz/factory_20.jpeg",
    logistics: "/cubenz/factory_21.jpeg",
};

const CHARGER_MODELS = [
    { kw: "60", model: "ZEUS 60", use: "Destination", desc: "Compact dual-gun DC fast charger designed for hotels, resorts and urban destinations." },
    { kw: "120", model: "ZEUS 120", use: "Highway Base", desc: "Twin-gun DC fast charger — the workhorse of Indian highway hubs." },
    { kw: "180", model: "ZEUS 180", use: "Highway Premium", desc: "Higher throughput for busy corridors and fleet handovers." },
    { kw: "240", model: "ZEUS 240", use: "Ultra Fast", desc: "For premium hubs, fleet depots and ultra-fast passenger charging." },
    { kw: "360", model: "ZEUS 360", use: "Future Ready", desc: "Built for next-gen long-range EVs and V2G-ready infrastructure." },
];

const HARDWARE_ADVANTAGES = [
    { icon: Cpu, title: "Modular Power Architecture", desc: "30 kW power modules that can be scaled from 60 kW to 360 kW — no charger replacement needed as your site grows." },
    { icon: ShieldCheck, title: "IP54 Weather-Sealed Chassis", desc: "Engineered for Indian summers, monsoons and coastal salt exposure. Operational range: -20°C to +55°C." },
    { icon: Layers, title: "Dual-Gun CCS2 + CHAdeMO", desc: "Universal compatibility with every EV sold in India — Tata, MG, Mahindra, Hyundai, BYD, Kia, BMW, Mercedes." },
    { icon: Activity, title: "OCPP 1.6J / 2.0.1 Native", desc: "Full open-protocol compliance — no vendor lock-in. Works with any CMS, roaming aggregator or app." },
    { icon: Wifi, title: "4G + Ethernet + Wi-Fi", desc: "Triple-redundant connectivity. Live telemetry every 5 seconds. Zero downtime from network outages." },
    { icon: Gauge, title: "97%+ Peak Efficiency", desc: "SiC (Silicon Carbide) power electronics deliver best-in-class efficiency — lower energy losses, higher partner margins." },
    { icon: Zap, title: "Dynamic Load Balancing", desc: "Intelligent power sharing across bays and phases — maximise concurrent sessions on any grid connection." },
    { icon: Boxes, title: "Field-Serviceable Modules", desc: "Hot-swappable power modules — a failed module replaces in <30 minutes, not days. Zero station downtime." },
];

const FACTORY_STATS = [
    { n: "10,000+", l: "sq. ft. facility", sub: "Fully integrated assembly, testing & QA under one roof" },
    { n: "60–360 kW", l: "range in-house", sub: "Complete power tier — no reliance on third-party OEMs" },
    { n: "100%", l: "in-country design", sub: "Designed, engineered and assembled in India" },
    { n: "500+", l: "units annual capacity", sub: "Ramping to 2,000+ units to power the E3 rollout" },
];

const CERTIFICATIONS = [
    { name: "ARAI · India Homologation", desc: "Certified for use in India's public charging infrastructure" },
    { name: "OCPP 1.6J / 2.0.1", desc: "Open Charge Point Protocol native — no vendor lock-in" },
    { name: "IEC 61851-1 / 61851-23", desc: "International EV charging safety & interoperability standards" },
    { name: "IEC 62196 (CCS2 / CHAdeMO)", desc: "Universal connector compatibility across every Indian EV" },
    { name: "CE / RoHS / ISI", desc: "European conformity, hazardous-substance-free, Indian standards mark" },
    { name: "ISO 9001:2015 QMS", desc: "Quality management system certified" },
];

const SOFTWARE = [
    { icon: Wifi, title: "CMS Platform", desc: "Central Management System with real-time monitoring, session tracking and remote diagnostics." },
    { icon: Activity, title: "Investor Dashboard", desc: "Live revenue, utilisation, ROI and carbon metrics for every station owner — on web and mobile." },
    { icon: Cpu, title: "Energy Management", desc: "Smart load balancing, demand-response and grid integration built-in." },
    { icon: Gauge, title: "99% Uptime Commitment", desc: "Preventive maintenance, remote monitoring, priority breakdown response — SLA-backed." },
    { icon: Sun, title: "Solar & Storage Ready", desc: "Solar integration, battery storage and DG backup for premium and remote locations." },
    { icon: Battery, title: "V2G Future Ready", desc: "Vehicle-to-grid protocol readiness — prepare for the next wave of energy services." },
];

export const Technology = () => {
    return (
        <div data-testid="technology-page">
            {/* HERO with Cubenz factory image */}
            <section className="relative min-h-[85vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${FACTORY.hero})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/50 to-stone-950/20" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 w-full">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs tracking-[0.2em] uppercase mb-8 animate-fade-up">
                        <Factory className="w-3.5 h-3.5" /> Powered by Cubenz Technology™
                    </div>
                    <h1 data-testid="tech-hero-title" className="font-serif text-white text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] max-w-5xl animate-fade-up">
                        Not a reseller. <br />An <span className="italic e3-gradient-text">OEM-backed network.</span>
                    </h1>
                    <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl mt-8 animate-fade-up">
                        Every E3 station is designed, manufactured and serviced by Cubenz — an Indian
                        EV charging infrastructure OEM. From SiC power electronics to weather-sealed
                        cabinets, everything is built in-house.
                    </p>
                </div>
            </section>

            {/* CUBENZ AT A GLANCE */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Cubenz at a Glance</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Built in India. For India. <span className="italic e3-gradient-text">By engineers.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Cubenz designs and assembles EV chargers end-to-end at its integrated
                                facility — power electronics, embedded firmware, cabinet fabrication,
                                testing and quality assurance under one roof.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 stagger" data-testid="tech-factory-stats">
                        {FACTORY_STATS.map((s, i) => (
                            <div key={i} className="e3-card-static" data-testid={`tech-stat-${i}`}>
                                <div className="font-serif text-4xl md:text-5xl font-light e3-gradient-text mb-3 leading-none">{s.n}</div>
                                <div className="text-sm font-semibold text-stone-900 mb-2">{s.l}</div>
                                <div className="text-xs text-stone-500 leading-relaxed">{s.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FACTORY GALLERY — hero + 3 grid */}
            <section className="py-16 md:py-24 px-6 md:px-10 bg-stone-950 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6 text-stone-400">Inside the Cubenz Factory</div>
                    <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] mb-14 max-w-3xl">
                        Precision assembly. In-house testing. Zero shortcuts.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="tech-factory-gallery">
                        <div className="md:col-span-8 rounded-2xl overflow-hidden aspect-[16/10] bg-stone-900">
                            <img src={FACTORY.assemblyLine} alt="Cubenz assembly line — technicians integrating power electronics" className="w-full h-full object-cover" />
                        </div>
                        <div className="md:col-span-4 rounded-2xl overflow-hidden aspect-[16/10] md:aspect-auto bg-stone-900">
                            <img src={FACTORY.detail1} alt="Cubenz internal wiring detail" className="w-full h-full object-cover" />
                        </div>
                        <div className="md:col-span-4 rounded-2xl overflow-hidden aspect-[16/10] bg-stone-900">
                            <img src={FACTORY.detail2} alt="Cubenz circuit board close-up" className="w-full h-full object-cover" />
                        </div>
                        <div className="md:col-span-4 rounded-2xl overflow-hidden aspect-[16/10] bg-stone-900">
                            <img src={FACTORY.finished} alt="Finished Cubenz ZEUS 60 chargers on factory floor" className="w-full h-full object-cover" />
                        </div>
                        <div className="md:col-span-4 rounded-2xl overflow-hidden aspect-[16/10] bg-stone-900">
                            <img src={FACTORY.packaged} alt="Cubenz charger packaged for shipment" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <p className="mt-8 text-xs text-stone-500 italic">Photographs from Cubenz's manufacturing facility · every E3 station begins here.</p>
                </div>
            </section>

            {/* WHY CUBENZ HARDWARE IS THE BEST */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Why Cubenz Hardware Wins</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                Eight reasons Cubenz chargers <span className="italic e3-gradient-text">outperform imports.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Imported chargers weren't designed for Indian conditions — 45°C summers, monsoon humidity, unstable grids, coastal salt. Cubenz was.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger" data-testid="tech-hardware-advantages">
                        {HARDWARE_ADVANTAGES.map((h, i) => {
                            const Icon = h.icon;
                            return (
                                <div key={h.title} className="e3-card group" data-testid={`tech-advantage-${i}`}>
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="w-11 h-11 rounded-xl e3-gradient-soft flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                                        </div>
                                        <div className="text-xs font-mono text-stone-400 tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                                    </div>
                                    <h3 className="font-serif text-lg font-medium text-stone-900 mb-3 leading-tight">{h.title}</h3>
                                    <p className="text-stone-600 text-sm leading-relaxed">{h.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ZEUS CHARGER LINE-UP */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">The ZEUS Series</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                60 kW to 360 kW. <span className="italic e3-gradient-text">One family.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                A single modular family that scales from destination to ultra-fast. Same interface, same CMS, same connectors — different power capacity.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 stagger" data-testid="tech-zeus-lineup">
                        {CHARGER_MODELS.map((c, i) => (
                            <div key={c.model} className="e3-card" data-testid={`tech-zeus-${i}`}>
                                <div className="flex items-baseline gap-1.5 mb-2">
                                    <span className="font-serif text-5xl font-light e3-gradient-text">{c.kw}</span>
                                    <span className="text-stone-500 text-base">kW</span>
                                </div>
                                <div className="text-emerald-700 font-bold text-xs tracking-widest uppercase mb-2">{c.model}</div>
                                <div className="text-xs text-stone-500 font-semibold mb-3">{c.use}</div>
                                <p className="text-stone-600 text-xs leading-relaxed">{c.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CERTIFICATIONS */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="e3-overline mb-6">Certifications & Standards</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-6">
                            Compliance built in. <span className="italic e3-gradient-text">Not bolted on.</span>
                        </h2>
                        <p className="text-stone-600 text-lg leading-relaxed">
                            Every Cubenz charger ships against international and Indian standards — safety, interoperability and quality all certified.
                        </p>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tech-certifications">
                        {CERTIFICATIONS.map((cert, i) => (
                            <div key={cert.name} className="p-5 rounded-xl border border-stone-200 bg-white hover:border-emerald-300 transition" data-testid={`tech-cert-${i}`}>
                                <div className="flex items-start gap-3">
                                    <Award className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="font-serif text-base font-medium text-stone-900">{cert.name}</div>
                                        <div className="text-xs text-stone-600 mt-1 leading-relaxed">{cert.desc}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SOFTWARE STACK */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-950 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6 text-stone-400">Software Ecosystem</div>
                    <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-16 max-w-4xl">
                        The chargers you can touch. The software that makes them <span className="italic e3-gradient-text">intelligent.</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                        {SOFTWARE.map((s) => {
                            const Icon = s.icon;
                            return (
                                <div key={s.title} className="rounded-2xl border border-stone-800 bg-stone-900/50 p-8 hover:border-emerald-500/50 hover:bg-stone-900 transition">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-6">
                                        <Icon className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-serif text-2xl font-medium mb-3">{s.title}</h3>
                                    <p className="text-stone-400 leading-relaxed">{s.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-6xl mx-auto e3-gradient-bg rounded-3xl p-12 md:p-20 text-white relative overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="relative">
                        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-6 max-w-3xl">
                            OEM-backed. Made in India. Built for the decade ahead.
                        </h2>
                        <p className="text-white/85 text-lg md:text-xl max-w-2xl mb-10">
                            Every E3 franchise partner gets direct access to Cubenz engineering, not a third-party service window.
                        </p>
                        <Link to="/franchise" data-testid="tech-cta" className="bg-white text-stone-900 px-8 py-3.5 rounded-full font-semibold hover:bg-stone-100 inline-flex items-center gap-2">
                            Explore Franchise Models <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};
